import * as React from "react";
import { Tab, TabList, TabPanel, Tabs } from "react-tabs";
import "react-tabs/style/react-tabs.css";
import { TabValidationIndicator, useTabValidation } from "./profile-form-components/TabValidationIndicator";
import { validateProfile, hasValidationErrors, validateJobTypes, validatePositionTypes, validateExperience } from "../utils/validation";
import { ValidationErrorSummary } from "./profile-form-components/ValidationErrorSummary";
import { SearchableMultiSelect, PhoneInput } from "../../../shared/components/forms";
import { ExperienceForm } from "../../../shared/forms/experience-education";
import axios from "axios";
import type { JobTypeSummary, PositionTypeSummary } from "../../../shared/types/domainModels";

interface Experience {
  id?: number;
  position?: string;
  company?: string;
  start_month?: string;
  start_year?: string;
  end_month?: string;
  end_year?: string;
  current?: boolean;
  description?: string;
  _destroy?: boolean;
  employer?: string; // For backwards compatibility
  title?: string; // For backwards compatibility
  start_date?: string; // For backwards compatibility
  end_date?: string; // For backwards compatibility
}

interface Profile {
  first_name: string;
  last_name: string;
  phone: string;
  zip: string;
  position_type_ids: number[];
  job_type_ids: number[];
  experiences_attributes: {
    employer: string;
    title: string;
    start_date: string;
    end_date: string;
    description: string;
    current: boolean;
  }[];
  resume: File | string;
  sms_optin: boolean;
}

interface ValidationErrors {
  [key: string]: string | null;
}

interface NestedValidationErrors {
  experiences: Array<{ [key: string]: string | null }>;
  educations: Array<{ [key: string]: string | null }>;
}

interface State {
  profile: Profile;
  tabIndex: number;
  experience: boolean;
  experience_selected: boolean;
  experience_array: Experience[];
  processing: boolean;
  error: boolean;
  validation_errors: ValidationErrors;
  nested_validation_errors: NestedValidationErrors;
  visited_tabs: Set<number>;
  touched_fields: Set<string>;
  submitted: boolean;
}

interface Props {
  job_types: JobTypeSummary[];
  hiring_types: PositionTypeSummary[];
  authenticity_token: string;
  profile?: Partial<Profile>; // Profile data from Rails
}

const initialState: State = {
  processing: false,
  error: false,
  experience_selected: false,
  experience: false,
  tabIndex: 0,
  experience_array: [],
  profile: {
    first_name: "",
    last_name: "",
    phone: "",
    zip: "",
    position_type_ids: [],
    job_type_ids: [],
    experiences_attributes: [],
    resume: "",
    sms_optin: true,
  },
  validation_errors: {
    first_name: null,
    last_name: null,
    phone: null,
    zip: null,
    positions: null,
    availability: null,
    experience_or_resume: null,
  },
  nested_validation_errors: {
    experiences: [],
    educations: [],
  },
  visited_tabs: new Set<number>([0]), // Mark the first tab as visited from the start
  touched_fields: new Set<string>(), // Track which fields have been interacted with
  submitted: false,
};

interface ValidationErrorProps {
  display: boolean;
  message: string;
  shouldShow: boolean; // Progressive validation control
}

const ValidationError: React.FC<ValidationErrorProps> = ({ display, message, shouldShow }) => {
  return (
    <React.Fragment>
      {display && shouldShow && (
        <div className="text-danger small mt-1">
          <i className="fas fa-exclamation-circle me-1"></i>
          {message}
        </div>
      )}
    </React.Fragment>
  );
};

interface SendProfileParams {
  profile: Profile;
  setProcessing: (processing: boolean) => void;
  setError: (error: boolean) => void;
  authenticity_token: string;
  state: State;
  setState: React.Dispatch<React.SetStateAction<State>>;
}

const sendProfile = ({
  profile,
  setProcessing,
  setError,
  authenticity_token,
  state,
  setState,
}: SendProfileParams): void => {
  // Run comprehensive validation
  const validationErrors = validateProfile(profile);
  const hasErrors = hasValidationErrors(validationErrors);

  if (hasErrors) {
    // Update state with validation errors and prevent submission
    setState(prevState => ({
      ...prevState,
      validation_errors: validationErrors,
    }));

    // Navigate to the first tab with errors
    const firstErrorTab = getFirstTabWithErrors(validationErrors);
    setState(prevState => ({
      ...prevState,
      tabIndex: firstErrorTab,
    }));

    // Scroll to form
    setTimeout(() => {
      const element = document.getElementById("profileformcontainer");
      element?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);

    return;
  }

  setProcessing(true);
  setError(false);

  let formData = new FormData();

  Object.entries(profile).forEach(([key, value]) => {
    const isArray = [
      "position_type_ids",
      "job_type_ids",
      "experiences_attributes",
    ].includes(key);

    // Resume
    if (key === "resume" && value !== "") {
      formData.append(`profile[${key}]`, value as File, (value as File).name);
    } else if (isArray) {
      // Positions
      if (key === "position_type_ids") {
        (value as number[]).forEach((pk) =>
          formData.append(`profile[${key}][]`, pk.toString())
        );
      }
      // Jobs
      if (key === "job_type_ids") {
        (value as number[]).forEach((pk) =>
          formData.append(`profile[${key}][]`, pk.toString())
        );
      }
      // Experience
      if (key === "experiences_attributes") {
        (value as Profile['experiences_attributes']).forEach((exp) => {
          formData.append(
            `profile[experiences_attributes][][employer]`,
            exp.employer
          );
          formData.append(
            `profile[experiences_attributes][][title]`,
            exp.title
          );
          formData.append(
            `profile[experiences_attributes][][start_date]`,
            exp.start_date
          );
          formData.append(
            `profile[experiences_attributes][][end_date]`,
            exp.end_date
          );
          formData.append(
            `profile[experiences_attributes][][description]`,
            exp.description
          );
          formData.append(
            `profile[experiences_attributes][][current]`,
            exp.current.toString()
          );
        });
      }
    } else {
      // Handle null/undefined values safely
      const stringValue = value != null ? value.toString() : '';
      formData.append(`profile[${key}]`, stringValue);
    }
  });

  const options = {
    headers: {
      "X-CSRF-Token": authenticity_token,
      "Content-type": "multipart/form-data",
    },
  };

  axios
    .post("/profiles", formData, options)
    .then((res) => {
      const url = res.request.responseURL;
      if (url) window.location.href = url;
    })
    .catch((err) => {
      setProcessing(false);
      setError(true);
    });
};

// Function to find the first tab with validation errors
const getFirstTabWithErrors = (validationErrors: ValidationErrors) => {
  const fieldToTab: Record<string, number> = {
    // General Info (Tab 0)
    'first_name': 0,
    'last_name': 0,
    'phone': 0,
    'zip': 0,

    // Positions (Tab 1)
    'positions': 1,
    'job_type_ids': 1,

    // Availability (Tab 2)
    'availability': 2,
    'position_type_ids': 2,

    // Experience (Tab 3)
    'experience_or_resume': 3,
    'experiences': 3,
  };

  // Check validation errors
  for (const field in validationErrors) {
    if (validationErrors[field]) {
      if (fieldToTab[field] !== undefined) {
        return fieldToTab[field];
      }
    }
  }

  return 0; // Default to first tab
};

const ProfileForm: React.FC<Props> = ({ job_types, hiring_types, authenticity_token, profile: railsProfile }) => {
  // Merge Rails profile with initial state, ensuring array fields are always defined
  const mergedProfile = {
    ...initialState.profile,
    ...railsProfile,
    // Ensure these array fields are always defined as arrays
    position_type_ids: railsProfile?.position_type_ids || [],
    job_type_ids: railsProfile?.job_type_ids || [],
    experiences_attributes: railsProfile?.experiences_attributes || [],
  };

  const [state, setState] = React.useState<State>({
    ...initialState,
    profile: mergedProfile
  });

  // Calculate tab validation status
  const tabValidation = useTabValidation(state.validation_errors, state.nested_validation_errors, state.profile);

  // Create separate validation status for individual tabs
  const positionsValidation = {
    hasErrors: !!state.validation_errors.positions,
    errorCount: state.validation_errors.positions ? 1 : 0,
    isComplete: state.profile.job_type_ids && state.profile.job_type_ids.length > 0 && !state.validation_errors.positions
  };

  const availabilityValidation = {
    hasErrors: !!state.validation_errors.availability,
    errorCount: state.validation_errors.availability ? 1 : 0,
    isComplete: state.profile.position_type_ids && state.profile.position_type_ids.length > 0 && !state.validation_errors.availability
  };

  // Run validation whenever profile data changes (simplified approach)
  React.useEffect(() => {
    // Always run validation, but on submit we set validation errors directly
    // so this effect should continue to work for progressive validation

    // For dropdown fields, run validation immediately when tabs are visited
    // This ensures validation errors clear when values are selected
    const allValidationErrors = validateProfile(state.profile);
    
    const filteredErrors: ValidationErrors = {};

    // Field to tab mapping
    const fieldToTab: { [key: string]: number } = {
      // General Info (Tab 0)
      'first_name': 0,
      'last_name': 0,
      'phone': 0,
      'zip': 0,

      // Positions (Tab 1)
      'positions': 1,

      // Availability (Tab 2)
      'availability': 2,

      // Experience (Tab 3)
      'experience_or_resume': 3,
      'experiences': 3,
    };

    // Include validation errors for:
    // 1. Fields from visited tabs that have been touched
    // 2. Dropdown fields (positions/availability) from visited tabs (regardless of touched status)
    Object.keys(allValidationErrors).forEach(field => {
      const tabIndex = fieldToTab[field];
      const fieldTouched = state.touched_fields.has(field) || state.touched_fields.has(`profile_${field}`);
      const isDropdownField = field === 'positions' || field === 'availability';
      
      if (tabIndex !== undefined && state.visited_tabs.has(tabIndex)) {
        // For dropdown fields, always include if tab is visited
        // For other fields, only include if touched (or if form was submitted)
        if (isDropdownField || fieldTouched || state.submitted) {
          filteredErrors[field] = allValidationErrors[field];
        }
      }
    });

    // Only update if validation errors have actually changed
    const currentErrors = JSON.stringify(state.validation_errors);
    const newErrors = JSON.stringify(filteredErrors);

    if (currentErrors !== newErrors) {
      setState(prevState => ({
        ...prevState,
        validation_errors: filteredErrors
      }));
    }
  }, [state.profile, state.visited_tabs, state.touched_fields, state.submitted]); // Run validation whenever profile data, visited tabs, touched fields, or submitted flag change

  // Run nested validation for experiences
  React.useEffect(() => {
    if (state.profile.experiences_attributes && state.profile.experiences_attributes.length > 0) {
      const experienceErrors: { [key: string]: { [key: string]: string | null } } = {};
      
      state.profile.experiences_attributes.forEach((experience, index) => {
        const errors = validateExperience(experience);
        if (Object.values(errors).some(error => error !== null)) {
          experienceErrors[index] = errors;
        }
      });

      // Only update if nested validation errors have actually changed
      const currentNestedErrors = JSON.stringify(state.nested_validation_errors.experiences);
      const newNestedErrors = JSON.stringify(experienceErrors);

      if (currentNestedErrors !== newNestedErrors) {
        setState(prevState => ({
          ...prevState,
          nested_validation_errors: {
            ...prevState.nested_validation_errors,
            experiences: Object.keys(experienceErrors).map(key => experienceErrors[key]) || []
          }
        }));
      }
    }
  }, [state.profile.experiences_attributes]); // Run nested validation when experiences change

  const selectTab = React.useCallback((tabIndex: number) => {
    setState(prevState => {
      // Mark the new tab as visited when we arrive at it
      const newVisitedTabs = new Set(prevState.visited_tabs);
      newVisitedTabs.add(tabIndex);

      // Scroll to form when tab changes
      setTimeout(() => {
        const element = document.getElementById("profileformcontainer");
        element?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);

      return {
        ...prevState,
        tabIndex,
        visited_tabs: newVisitedTabs,
      };
    });
  }, []);

  const nextTab = () => {
    const element = document.getElementById("profileformcontainer");
    element?.scrollIntoView({ behavior: "smooth", block: "start" });

    setTimeout(() => {
      setState(prevState => {
        const newTabIndex = prevState.tabIndex + 1;
        // Mark the new tab as visited when we arrive at it
        const newVisitedTabs = new Set(prevState.visited_tabs);
        newVisitedTabs.add(newTabIndex);

        return {
          ...prevState,
          tabIndex: newTabIndex,
          visited_tabs: newVisitedTabs,
        };
      });
    }, 200);
  };

  const unselectExperienceAndResume = React.useCallback(() => {
    setState(prevState => ({
      ...prevState,
      experience: false,
      experience_selected: false,
      experience_array: [],
      nested_validation_errors: {
        ...prevState.nested_validation_errors,
        experiences: [],
      },
      profile: {
        ...prevState.profile,
        experiences_attributes: [],
        resume: ""
      }
    }));
  }, []);

  const selectExperience = React.useCallback(() => {
    setState(prevState => {
      // Create a blank experience entry
      const blankExperience: Experience = {
        id: Date.now(), // Use timestamp as temporary ID
        title: "",
        company: "",
        employer: "",
        position: "",
        start_date: "",
        end_date: "",
        start_month: "",
        start_year: "",
        end_month: "",
        end_year: "",
        description: "",
        current: false,
      };

      const newTouchedFields = new Set(prevState.touched_fields);
      newTouchedFields.add('experience_or_resume');

      return {
        ...prevState,
        experience: true,
        experience_selected: true,
        experience_array: [blankExperience],
        touched_fields: newTouchedFields,
        profile: {
          ...prevState.profile,
          experiences_attributes: [{
            employer: "",
            title: "",
            start_date: "",
            end_date: "",
            description: "",
            current: false,
          }],
        },
      };
    });
  }, []);

  const selectResume = React.useCallback(() => {
    setState(prevState => {
      const newTouchedFields = new Set(prevState.touched_fields);
      newTouchedFields.add('experience_or_resume');

      return {
        ...prevState,
        experience: false,
        experience_selected: true,
        touched_fields: newTouchedFields,
      };
    });
  }, []);

  const updateProfile = React.useCallback((event: { target: { name: string; value: string } }) => {
    const { name, value } = event.target;
    let val: string | undefined;
    if (name === "zip") {
      const re = /^[0-9\b]+$/;
      if ((value === "" || re.test(value)) && value.length < 6) val = value;
    } else {
      val = value;
    }

    if (val !== undefined) {
      setState((prevState) => {
        const newTouchedFields = new Set(prevState.touched_fields);
        newTouchedFields.add(name);
        return {
          ...prevState,
          profile: { ...prevState.profile, [name]: val },
          touched_fields: newTouchedFields,
        };
      });
    }
  }, []);

  const updateProfileCheckboxes = React.useCallback(<K extends keyof Profile>(name: K, value: Profile[K]) => {
    setState(prevState => {
      const newTouchedFields = new Set(prevState.touched_fields);
      newTouchedFields.add(name as string);

      // Also mark the validation field names as touched for proper filtering
      if (name === 'job_type_ids') {
        newTouchedFields.add('positions'); // Mark validation field as touched
      } else if (name === 'position_type_ids') {
        newTouchedFields.add('availability'); // Mark validation field as touched
      }

      // Mark the relevant tab as visited when dropdown fields are touched
      const newVisitedTabs = new Set(prevState.visited_tabs);
      if (name === 'job_type_ids') {
        newVisitedTabs.add(1); // Positions tab
      } else if (name === 'position_type_ids') {
        newVisitedTabs.add(2); // Availability tab
      }

      return {
        ...prevState,
        profile: { ...prevState.profile, [name]: value },
        touched_fields: newTouchedFields,
        visited_tabs: newVisitedTabs,
      };
    });
  }, []);

  const updateExperience = React.useCallback((experience: Experience[]) => {
    setState(prevState => {
      const newProfile = {
        ...prevState.profile,
        experiences_attributes: experience.map((exp) => ({
          employer: exp.company || exp.employer || "",
          title: exp.position || exp.title || "",
          start_date: exp.start_month && exp.start_year ? `${exp.start_year}-${exp.start_month}-01` : exp.start_date || "",
          end_date: exp.end_month && exp.end_year ? `${exp.end_year}-${exp.end_month}-01` : exp.end_date || "",
          description: exp.description || "",
          current: exp.current || false,
        })),
      };

      return {
        ...prevState,
        experience_array: experience,
        profile: newProfile,
      };
    });
  }, []);

  const handleFileChange = React.useCallback((files: FileList | null) => {
    if (files && files.length > 0) {
      setState(prevState => ({
        ...prevState,
        profile: { ...prevState.profile, resume: files[0] },
      }));
    }
  }, []);

  const setProcessing = React.useCallback((processing: boolean) => {
    setState(prevState => ({ ...prevState, processing }));
  }, []);

  const setError = React.useCallback((error: boolean) => {
    setState(prevState => ({ ...prevState, error }));
  }, []);

  const submitForm = () => {
    // Mark all tabs as visited and all fields as touched when trying to submit
    const allFields = new Set(['first_name', 'last_name', 'phone', 'zip', 'job_type_ids', 'position_type_ids', 'experience_or_resume']);

    const validationResults = validateProfile(state.profile);

    // Create the updated state with all tabs visited, fields touched, AND validation errors
    const updatedState = {
      ...state,
      visited_tabs: new Set([0, 1, 2, 3]),
      touched_fields: allFields,
      validation_errors: validationResults,
      submitted: true, // Add flag to indicate form was submitted
    };

    // Update the component state with validation errors
    setState(updatedState);

    // Check for nested validation errors (like individual experience field validation)
    // Always validate experience fields if they were filled out, regardless of resume upload
    const hasNestedErrors = state.nested_validation_errors.experiences?.some(exp => exp && Object.values(exp).some(error => error));

    // If there are validation errors OR nested errors, don't proceed with submission
    if (hasValidationErrors(validationResults) || hasNestedErrors) {
      return;
    }

    // Call sendProfile only if validation passes
    const { profile } = state;
    sendProfile({
      profile,
      setProcessing,
      setError,
      authenticity_token,
      state: updatedState,
      setState,
    });
  };

  const tabStyles = "react-tabs__tab text-center flex-grow-1";

  return (
    <div id="profileformcontainer">
      <style>
        {`
          .selection-card:hover {
            transform: translateY(-2px);
          }
          .upload-area:hover {
            border-color: #007bff !important;
            background-color: #f8f9ff;
          }
        `}
      </style>
      {/* Display validation errors at the top of the form only for visited tabs */}
      {state.visited_tabs.size > 0 && (
        <ValidationErrorSummary
          validationErrors={state.validation_errors}
          nestedValidationErrors={state.nested_validation_errors}
          experiences={state.profile.experiences_attributes}
          educations={[]}
          dispatch={({ type, payload }) => {
            if (type === 'SET_TAB_INDEX') {
              selectTab(payload);
            }
          }}
        />
      )}

      {state.processing && (
        <div className="fixed-top d-flex align-items-center justify-content-center" style={{ height: '100vh', backgroundColor: 'rgba(255,255,255,0.8)', zIndex: 9999 }}>
          <div className="d-flex align-items-center">
            <div className="spinner-border text-primary me-3" role="status" aria-hidden="true"></div>
            <span className="h5 mb-0">Creating your profile...</span>
          </div>
        </div>
      )}

      {state.error && (
        <div className="alert alert-danger mb-4" role="alert">
          <h4 className="alert-heading">Error!</h4>
          <p>There was an error creating your profile. Please check your information and try again.</p>
          <hr />
          <p className="mb-0">If the problem persists, please contact support.</p>
        </div>
      )}

      {!state.processing && (
        <Tabs
          selectedIndex={state.tabIndex}
          onSelect={selectTab}
          style={{ maxWidth: 800, margin: "auto" }}
        >
          <TabList className="d-flex justify-content-between gap-0 mb-4">
            <Tab className={tabStyles}>
              <i className="fas fa-user me-2"></i>
              <span className="d-none d-md-inline">General Info</span>
              <span className="d-md-none">General</span>
              <TabValidationIndicator {...tabValidation.generalInfo} />
            </Tab>
            <Tab className={tabStyles}>
              <i className="fas fa-briefcase me-2"></i>
              <span className="d-none d-md-inline">Positions</span>
              <span className="d-md-none">Jobs</span>
              <TabValidationIndicator {...positionsValidation} />
            </Tab>
            <Tab className={tabStyles}>
              <i className="fas fa-calendar me-2"></i>
              <span className="d-none d-md-inline">Availability</span>
              <span className="d-md-none">Avail</span>
              <TabValidationIndicator {...availabilityValidation} />
            </Tab>
            <Tab className={tabStyles}>
              <i className="fas fa-graduation-cap me-2"></i>
              <span className="d-none d-md-inline">Experience</span>
              <span className="d-md-none">Exp</span>
              <TabValidationIndicator {...tabValidation.experience} />
            </Tab>
          </TabList>

          {/* General Information */}
          <TabPanel>
            <div className="card">
              <div className="card-header bg-primary text-white">
                <h5 className="mb-0">
                  <i className="fas fa-user me-2"></i>
                  General Information
                </h5>
              </div>
              <div className="card-body">
                <form className="profile-form">
                  <div className="row">
                    {/* First Name */}
                    <div className="col-md-6">
                      <div className="form-group mb-3">
                        <label htmlFor="profile_first_name_field" className="form-label">
                          First Name <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className={`form-control ${state.validation_errors.first_name && state.visited_tabs.has(0) && state.touched_fields.has('first_name') ? 'is-invalid' : ''}`}
                          name="first_name"
                          id="profile_first_name_field"
                          placeholder="First Name"
                          value={state.profile.first_name}
                          onChange={updateProfile}
                          aria-required="true"
                        />
                        <ValidationError
                          display={!!state.validation_errors.first_name}
                          message={state.validation_errors.first_name || ""}
                          shouldShow={state.visited_tabs.has(0) && state.touched_fields.has('first_name')}
                        />
                      </div>
                    </div>

                    {/* Last Name */}
                    <div className="col-md-6">
                      <div className="form-group mb-3">
                        <label htmlFor="profile_last_name_field" className="form-label">
                          Last Name <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className={`form-control ${state.validation_errors.last_name && state.visited_tabs.has(0) && state.touched_fields.has('last_name') ? 'is-invalid' : ''}`}
                          name="last_name"
                          id="profile_last_name_field"
                          placeholder="Last Name"
                          value={state.profile.last_name}
                          onChange={updateProfile}
                          aria-required="true"
                        />
                        <ValidationError
                          display={!!state.validation_errors.last_name}
                          message={state.validation_errors.last_name || ""}
                          shouldShow={state.visited_tabs.has(0) && state.touched_fields.has('last_name')}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    {/* Phone */}
                    <div className="col-md-6">
                      <div className="form-group mb-3">
                        <label htmlFor="profile_phone_field" className="form-label">
                          Phone <span className="text-danger">*</span>
                        </label>
                        <PhoneInput
                          name="phone"
                          id="profile_phone_field"
                          className={`form-control ${state.validation_errors.phone && state.visited_tabs.has(0) && state.touched_fields.has('phone') ? 'is-invalid' : ''}`}
                          placeholder="Phone"
                          value={state.profile.phone}
                          onChange={updateProfile}
                          aria-required={true}
                        />
                        <ValidationError
                          display={!!state.validation_errors.phone}
                          message={state.validation_errors.phone || ""}
                          shouldShow={state.visited_tabs.has(0) && state.touched_fields.has('phone')}
                        />
                      </div>
                    </div>

                    {/* Zip Code */}
                    <div className="col-md-6">
                      <div className="form-group mb-3">
                        <label htmlFor="profile_zip_field" className="form-label">
                          ZIP Code <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className={`form-control ${state.validation_errors.zip && state.visited_tabs.has(0) && state.touched_fields.has('zip') ? 'is-invalid' : ''}`}
                          name="zip"
                          id="profile_zip_field"
                          placeholder="ZIP Code"
                          value={state.profile.zip}
                          onChange={updateProfile}
                          aria-required="true"
                          maxLength={5}
                          pattern="[0-9]*"
                        />
                        <ValidationError
                          display={!!state.validation_errors.zip}
                          message={state.validation_errors.zip || ""}
                          shouldShow={state.visited_tabs.has(0) && state.touched_fields.has('zip')}
                        />
                      </div>
                    </div>
                  </div>
                </form>
              </div>
              <div className="card-footer">
                <div className="d-flex justify-content-center">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={nextTab}
                    disabled={state.processing}
                  >
                    Continue to Positions
                    <i className="fas fa-arrow-right ms-2"></i>
                  </button>
                </div>
              </div>
            </div>
          </TabPanel>

          {/* Positions */}
          <TabPanel>
            <div className="card">
              <div className="card-header bg-primary text-white">
                <h5 className="mb-0">
                  <i className="fas fa-briefcase me-2"></i>
                  Position Types
                </h5>
              </div>
              <div className="card-body">
                <SearchableMultiSelect<number>
                  options={job_types.map(jobType => ({
                    value: jobType.id,
                    label: jobType.title
                  }))}
                  value={state.profile.job_type_ids}
                  onChange={(selectedValues) => updateProfileCheckboxes('job_type_ids', selectedValues as number[])}
                  placeholder="Search and select job types..."
                  label="Select Your Position(s)"
                  required={true}
                  isMulti={true}
                  helpText="Choose all positions you're interested in. You can search and select multiple options."
                  error={state.validation_errors.positions || null}
                />
              </div>
              <div className="card-footer">
                <div className="d-flex justify-content-between">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => selectTab(0)}
                  >
                    <i className="fas fa-arrow-left me-2"></i>
                    Back
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={nextTab}
                    disabled={state.processing}
                  >
                    Continue to Availability
                    <i className="fas fa-arrow-right ms-2"></i>
                  </button>
                </div>
              </div>
            </div>
          </TabPanel>

          {/* Availability */}
          <TabPanel>
            <div className="card">
              <div className="card-header bg-primary text-white">
                <h5 className="mb-0">
                  <i className="fas fa-calendar me-2"></i>
                  Hiring Availability
                </h5>
              </div>
              <div className="card-body">
                <SearchableMultiSelect<number>
                  options={hiring_types.map(hiringType => ({
                    value: hiringType.id,
                    label: hiringType.name
                  }))}
                  value={state.profile.position_type_ids}
                  onChange={(selectedValues) => updateProfileCheckboxes('position_type_ids', selectedValues as number[])}
                  placeholder="Search and select availability types..."
                  label="Select Your Availability"
                  required={true}
                  isMulti={true}
                  helpText="Choose all hiring types you're available for (e.g., Full-time, Part-time, Temporary)."
                  error={state.validation_errors.availability || null}
                />

                {/* SMS Opt-in */}
                <div className="mt-4 p-3 bg-light rounded">
                  <div className="form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      name="sms_optin"
                      id="profile_sms_optin"
                      checked={state.profile.sms_optin}
                      onChange={(e) =>
                        updateProfileCheckboxes(e.target.name as keyof Profile, e.target.checked)
                      }
                    />
                    <label className="form-check-label" htmlFor="profile_sms_optin">
                      <strong>Receive SMS notifications</strong> when dental offices are interested in your application or temp availability.
                    </label>
                  </div>
                  {!state.profile.sms_optin && (
                    <div className="text-danger small mt-2">
                      <i className="fas fa-exclamation-triangle me-1"></i>
                      By opting out of SMS communications, you won't receive any SMS alerts when dental offices invite you to apply or temp.
                    </div>
                  )}
                </div>
              </div>
              <div className="card-footer">
                <div className="d-flex justify-content-between">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => selectTab(1)}
                  >
                    <i className="fas fa-arrow-left me-2"></i>
                    Back
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={nextTab}
                  >
                    Continue to Experience
                    <i className="fas fa-arrow-right ms-2"></i>
                  </button>
                </div>
              </div>
            </div>
          </TabPanel>

          {/* Experience */}
          <TabPanel>
            <div className="card">
              <div className="card-header bg-primary text-white">
                <h5 className="mb-0">
                  <i className="fas fa-graduation-cap me-2"></i>
                  Experience or Resume
                </h5>
              </div>
              <div className="card-body">
                {!state.experience_selected && (
                  <div className="text-center py-5">
                    <div className="mb-4">
                      <i className="fas fa-clipboard-list fa-3x text-primary mb-3"></i>
                      <h4 className="mb-3">How would you like to share your experience?</h4>
                      <p className="text-muted lead">Choose the method that works best for you</p>
                    </div>

                    <div className="row justify-content-center">
                      <div className="col-lg-5 col-md-6 mb-4">
                        <div
                          className="card h-100 border-2 selection-card"
                          style={{ cursor: 'pointer', transition: 'all 0.3s ease' }}
                          onClick={selectExperience}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.borderColor = '#007bff';
                            e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,123,255,0.2)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.borderColor = '#dee2e6';
                            e.currentTarget.style.boxShadow = 'none';
                          }}
                        >
                          <div className="card-body text-center p-4">
                            <div className="mb-3">
                              <i className="fas fa-keyboard fa-3x text-primary"></i>
                            </div>
                            <h5 className="card-title mb-3">Enter Experience Details</h5>
                            <p className="card-text text-muted">
                              Manually enter your work experience with detailed job descriptions,
                              responsibilities, and achievements.
                            </p>
                            <div className="mt-3">
                              <span className="badge bg-primary">Recommended</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="col-lg-5 col-md-6 mb-4">
                        <div
                          className="card h-100 border-2 selection-card"
                          style={{ cursor: 'pointer', transition: 'all 0.3s ease' }}
                          onClick={selectResume}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.borderColor = '#007bff';
                            e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,123,255,0.2)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.borderColor = '#dee2e6';
                            e.currentTarget.style.boxShadow = 'none';
                          }}
                        >
                          <div className="card-body text-center p-4">
                            <div className="mb-3">
                              <i className="fas fa-file-upload fa-3x text-success"></i>
                            </div>
                            <h5 className="card-title mb-3">Upload Resume</h5>
                            <p className="card-text text-muted">
                              Upload your existing resume (PDF, DOC, or DOCX) and we'll
                              extract your experience information.
                            </p>
                            <div className="mt-3">
                              <span className="badge bg-success">Quick & Easy</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {state.experience_selected && state.experience && (
                  <div>
                    <div className="d-flex justify-content-between align-items-center mb-4">
                      <div>
                        <h5 className="mb-1">
                          <i className="fas fa-keyboard me-2 text-primary"></i>
                          Experience Details
                        </h5>
                        <p className="text-muted mb-0">Add your work experience below</p>
                      </div>
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => {
                          setState(prevState => ({
                            ...prevState,
                            experience: false,
                            experience_selected: false,
                            experience_array: [],
                            profile: {
                              ...prevState.profile,
                              experiences_attributes: [],
                              resume: ""
                            }
                          }));
                        }}
                      >
                        <i className="fas fa-arrow-left me-1"></i>
                        Change Method
                      </button>
                    </div>

                    <ExperienceForm
                      experiences={state.experience_array}
                      onExperiencesChange={updateExperience}
                      validationErrors={state.nested_validation_errors.experiences.reduce((acc, errors, index) => {
                        acc[index] = errors || {};
                        return acc;
                      }, {} as { [index: number]: { [field: string]: string | null } })}
                      onValidationChange={(index, field, error) => {
                        setState(prevState => {
                          const newNestedErrors = { ...prevState.nested_validation_errors };
                          if (!newNestedErrors.experiences[index]) {
                            newNestedErrors.experiences[index] = {};
                          }
                          newNestedErrors.experiences[index][field] = error;
                          return {
                            ...prevState,
                            nested_validation_errors: newNestedErrors,
                          };
                        });
                      }}
                      showValidationErrors={state.visited_tabs.has(3)}
                    />
                  </div>
                )}

                {state.experience_selected && !state.experience && (
                  <div>
                    <div className="d-flex justify-content-between align-items-center mb-4">
                      <div>
                        <h5 className="mb-1">
                          <i className="fas fa-file-upload me-2 text-success"></i>
                          Resume Upload
                        </h5>
                        <p className="text-muted mb-0">Upload your resume file</p>
                      </div>
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => {
                          setState(prevState => ({
                            ...prevState,
                            experience: false,
                            experience_selected: false,
                            experience_array: [],
                            profile: {
                              ...prevState.profile,
                              experiences_attributes: [],
                              resume: ""
                            }
                          }));
                        }}
                      >
                        <i className="fas fa-arrow-left me-1"></i>
                        Change Method
                      </button>
                    </div>

                    <div className="text-center py-4">
                      <div className="upload-area border-2 border-dashed rounded p-5" style={{ borderColor: '#dee2e6' }}>
                        <i className="fas fa-cloud-upload-alt fa-3x text-muted mb-3"></i>
                        <h6 className="mb-3">Choose your resume file</h6>
                        <p className="text-muted mb-4">Supported formats: PDF, DOC, DOCX (Max 10MB)</p>

                        <label htmlFor="fileUpload" className="btn btn-primary btn-lg">
                          <i className="fas fa-upload me-2"></i>
                          {!state.profile.resume ? "Choose File" : "Change File"}
                        </label>
                        <input
                          type="file"
                          id="fileUpload"
                          style={{ display: "none" }}
                          accept=".pdf,.doc,.docx"
                          onChange={(e) => handleFileChange(e.target.files)}
                        />
                      </div>

                      {state.profile.resume instanceof File && (
                        <div className="alert alert-success mt-4">
                          <div className="d-flex align-items-center">
                            <i className="fas fa-check-circle fa-2x me-3 text-success"></i>
                            <div className="text-start">
                              <h6 className="mb-1">File uploaded successfully!</h6>
                              <p className="mb-0">
                                <strong>{state.profile.resume.name}</strong>
                                <span className="text-muted"> ({(state.profile.resume.size / 1024 / 1024).toFixed(2)} MB)</span>
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <ValidationError
                  display={!!state.validation_errors.experience_or_resume}
                  message={state.validation_errors.experience_or_resume || ""}
                  shouldShow={state.visited_tabs.has(3) && state.touched_fields.has('experience_or_resume')}
                />
              </div>
              <div className="card-footer">
                <div className="d-flex justify-content-between">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => selectTab(2)}
                  >
                    <i className="fas fa-arrow-left me-2"></i>
                    Back
                  </button>
                  <button
                    type="button"
                    className="btn btn-success btn-lg"
                    onClick={submitForm}
                    disabled={state.processing}
                  >
                    <i className="fas fa-save me-2"></i>
                    Create Profile
                  </button>
                </div>
                {(hasValidationErrors(state.validation_errors) || state.nested_validation_errors.experiences?.some(exp => exp && Object.values(exp).some(error => error))) && (
                  <div className="alert alert-warning mt-3" role="alert">
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    Please fix the validation errors above before creating your profile.
                  </div>
                )}
              </div>
            </div>
          </TabPanel>
        </Tabs>
      )}
    </div>
  );
};

export default ProfileForm;
